import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaBirthdayCake,
  FaGraduationCap,
  FaClock,
  FaTrash,
  FaSync,
  FaExclamationTriangle,
  FaCalendarTimes,
  FaEye,
  FaTimes,
} from "react-icons/fa";
import {
  getIncompleteAppointments,
  deleteIncompleteAppointment,
} from "../utils/emailUtils";
import "./IncompleteAppointments.css";

const IncompleteAppointments = () => {
  const [incompleteAppointments, setIncompleteAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchIncompleteAppointments();
  }, []);

  const fetchIncompleteAppointments = async () => {
    setLoading(true);
    try {
      const appointments = await getIncompleteAppointments();
      setIncompleteAppointments(appointments);
    } catch (error) {
      console.error("Error fetching incomplete appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (appointmentId) => {
    if (
      window.confirm(
        "Bu yarım kalan randevuyu silmek istediğinizden emin misiniz?"
      )
    ) {
      try {
        await deleteIncompleteAppointment(appointmentId);
        await fetchIncompleteAppointments(); // Refresh the list
      } catch (error) {
        console.error("Error deleting incomplete appointment:", error);
        alert("Randevu silinirken bir hata oluştu.");
      }
    }
  };

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedAppointment(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Bilinmiyor";

    let date;
    try {
      if (timestamp && typeof timestamp === "object" && timestamp.seconds) {
        // Firestore timestamp
        date = new Date(timestamp.seconds * 1000);
      } else if (
        timestamp &&
        typeof timestamp === "object" &&
        timestamp._seconds
      ) {
        // Alternative Firestore timestamp format
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp) {
        // Regular timestamp or string
        date = new Date(timestamp);
      } else {
        return "Bilinmiyor";
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Geçersiz tarih";
      }

      return date.toLocaleDateString("tr-TR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting date:", error, timestamp);
      return "Geçersiz tarih";
    }
  };

  const getTimeSinceLastActivity = (timestamp) => {
    if (!timestamp) return "Bilinmiyor";

    let date;
    try {
      if (timestamp && typeof timestamp === "object" && timestamp.seconds) {
        // Firestore timestamp
        date = new Date(timestamp.seconds * 1000);
      } else if (
        timestamp &&
        typeof timestamp === "object" &&
        timestamp._seconds
      ) {
        // Alternative Firestore timestamp format
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp) {
        // Regular timestamp or string
        date = new Date(timestamp);
      } else {
        return "Bilinmiyor";
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Geçersiz tarih";
      }

      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));

      if (diffInMinutes < 0) {
        return "Gelecek tarih";
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes} dakika önce`;
      } else if (diffInMinutes < 1440) {
        const hours = Math.floor(diffInMinutes / 60);
        return `${hours} saat önce`;
      } else {
        const days = Math.floor(diffInMinutes / 1440);
        return `${days} gün önce`;
      }
    } catch (error) {
      console.error("Error calculating time since activity:", error, timestamp);
      return "Geçersiz tarih";
    }
  };

  const getEducationLabel = (education) => {
    const educationMap = {
      "lisans-ogrenci": "Lisans Öğrencisi",
      "lisans-mezunu": "Lisans Mezunu",
      "12-sinif": "12. Sınıf",
      diger: "Diğer",
    };
    return educationMap[education] || education;
  };

  if (loading) {
    return (
      <div className="incomplete-appointments">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Yarım kalan randevular yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="incomplete-appointments">
      <div className="incomplete-header">
        <div className="header-content">
          <FaCalendarTimes className="header-icon" />
          <div className="header-text">
            <h2>Yarım Kalan Randevular</h2>
            <p>
              Form doldurup tarih-saat seçmeden ayrılan kullanıcılar. Bu
              kişilerle iletişime geçerek randevu tamamlanmasını
              sağlayabilirsiniz.
            </p>
          </div>
        </div>
        <button
          className="refresh-button"
          onClick={fetchIncompleteAppointments}
          disabled={loading}
        >
          <FaSync />
          Yenile
        </button>
      </div>

      <div className="incomplete-stats">
        <div className="stat-card">
          <div className="stat-number">{incompleteAppointments.length}</div>
          <div className="stat-label">Toplam Yarım Kalan</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-number">
            {
              incompleteAppointments.filter((app) => {
                try {
                  let date;
                  if (
                    app.lastActivity &&
                    typeof app.lastActivity === "object" &&
                    app.lastActivity.seconds
                  ) {
                    date = new Date(app.lastActivity.seconds * 1000);
                  } else if (
                    app.lastActivity &&
                    typeof app.lastActivity === "object" &&
                    app.lastActivity._seconds
                  ) {
                    date = new Date(app.lastActivity._seconds * 1000);
                  } else if (app.lastActivity) {
                    date = new Date(app.lastActivity);
                  } else {
                    return false;
                  }

                  if (isNaN(date.getTime())) return false;

                  return new Date() - date > 24 * 60 * 60 * 1000;
                } catch (error) {
                  return false;
                }
              }).length
            }
          </div>
          <div className="stat-label">24+ Saat Geçmiş</div>
        </div>
      </div>

      {incompleteAppointments.length === 0 ? (
        <div className="no-appointments">
          <FaCalendarTimes className="no-appointments-icon" />
          <h3>Yarım Kalan Randevu Bulunamadı</h3>
          <p>Tüm kullanıcılar randevularını tamamlamış görünüyor.</p>
        </div>
      ) : (
        <div className="appointments-grid">
          {incompleteAppointments.map((appointment) => (
            <div key={appointment.id} className="appointment-card">
              <div className="appointment-header">
                <div className="user-info">
                  <FaUser className="user-icon" />
                  <div className="user-details">
                    <h3>
                      {appointment.firstName} {appointment.lastName}
                    </h3>
                    <span className="last-activity">
                      {getTimeSinceLastActivity(appointment.lastActivity)}
                    </span>
                  </div>
                </div>
                <div className="appointment-actions">
                  <button
                    className="action-btn view-btn"
                    onClick={() => handleViewDetails(appointment)}
                    title="Detayları Görüntüle"
                  >
                    <FaEye />
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => handleDelete(appointment.id)}
                    title="Sil"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              <div className="appointment-content">
                <div className="contact-info">
                  <div className="contact-item">
                    <FaEnvelope className="contact-icon" />
                    <span>{appointment.email}</span>
                  </div>
                  <div className="contact-item">
                    <FaPhone className="contact-icon" />
                    <span>{appointment.phone}</span>
                  </div>
                </div>

                <div className="additional-info">
                  <div className="info-item">
                    <FaBirthdayCake className="info-icon" />
                    <span>{appointment.age} yaşında</span>
                  </div>
                  <div className="info-item">
                    <FaGraduationCap className="info-icon" />
                    <span>{getEducationLabel(appointment.education)}</span>
                  </div>
                </div>

                <div className="time-info">
                  <div className="time-item">
                    <FaClock className="time-icon" />
                    <div className="time-details">
                      <span className="time-label">Form Doldurma:</span>
                      <span className="time-value">
                        {formatDate(appointment.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {(() => {
                try {
                  let date;
                  if (
                    appointment.lastActivity &&
                    typeof appointment.lastActivity === "object" &&
                    appointment.lastActivity.seconds
                  ) {
                    date = new Date(appointment.lastActivity.seconds * 1000);
                  } else if (
                    appointment.lastActivity &&
                    typeof appointment.lastActivity === "object" &&
                    appointment.lastActivity._seconds
                  ) {
                    date = new Date(appointment.lastActivity._seconds * 1000);
                  } else if (appointment.lastActivity) {
                    date = new Date(appointment.lastActivity);
                  } else {
                    return false;
                  }

                  if (isNaN(date.getTime())) return false;

                  return new Date() - date > 24 * 60 * 60 * 1000;
                } catch (error) {
                  return false;
                }
              })() && (
                <div className="warning-badge">
                  <FaExclamationTriangle />
                  24+ Saat Geçmiş
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedAppointment && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Kullanıcı Detayları</h3>
              <button className="modal-close" onClick={closeDetailModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h4>Kişisel Bilgiler</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Ad Soyad:</label>
                    <span>
                      {selectedAppointment.firstName}{" "}
                      {selectedAppointment.lastName}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>E-posta:</label>
                    <span>{selectedAppointment.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>Telefon:</label>
                    <span>{selectedAppointment.phone}</span>
                  </div>
                  <div className="detail-item">
                    <label>Yaş:</label>
                    <span>{selectedAppointment.age}</span>
                  </div>
                  <div className="detail-item">
                    <label>Eğitim Durumu:</label>
                    <span>
                      {getEducationLabel(selectedAppointment.education)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Zaman Bilgileri</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Form Doldurma Tarihi:</label>
                    <span>{formatDate(selectedAppointment.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Son Aktivite:</label>
                    <span>{formatDate(selectedAppointment.lastActivity)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Geçen Süre:</label>
                    <span>
                      {getTimeSinceLastActivity(
                        selectedAppointment.lastActivity
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Öneriler</h4>
                <div className="suggestions">
                  <p>Bu kullanıcıyla iletişime geçmek için:</p>
                  <ul>
                    <li>
                      E-posta gönderebilirsiniz: {selectedAppointment.email}
                    </li>
                    <li>
                      Telefon ile arayabilirsiniz: {selectedAppointment.phone}
                    </li>
                    <li>Randevu tamamlama konusunda yardım sunabilirsiniz</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncompleteAppointments;
